import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout, ClairName } from '../components/layout/DashboardLayout';
import { goalsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { Plus, Target, CheckCircle2, Circle, Trash2, ChevronDown, ChevronUp, Calendar, Pencil, X, ArrowUp, ArrowDown, GripVertical, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

export default function GoalsPage() {
  const [searchParams] = useSearchParams();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null); // null = creating new, object = editing
  const [expandedGoals, setExpandedGoals] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, goalId: null });
  const [completeConfirm, setCompleteConfirm] = useState({ open: false, goalId: null });
  
  const [goalData, setGoalData] = useState({
    title: '',
    description: '',
    target_date: '',
    steps: [],
    notes: ''
  });
  const [newStep, setNewStep] = useState('');

  const loadGoals = useCallback(async () => {
    try {
      const response = await goalsAPI.getAll();
      setGoals(response.data);
      const expanded = {};
      response.data.forEach(g => { expanded[g.id] = true; });
      setExpandedGoals(expanded);
    } catch (error) {
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGoals();
    
    // Check if we have a prefilled goal from CLaiR
    const prefillGoal = sessionStorage.getItem('prefillGoal');
    const isNewGoal = searchParams.get('new') === 'true';
    
    if (prefillGoal && isNewGoal) {
      try {
        const goal = JSON.parse(prefillGoal);
        setGoalData({
          title: goal.title || '',
          description: goal.description || '',
          target_date: goal.target_date || '',
          steps: goal.steps?.map(text => ({ text, completed: false })) || [],
          notes: goal.notes || ''
        });
        setDialogOpen(true);
        sessionStorage.removeItem('prefillGoal');
        toast.info('Goal from CLaiR ready to customize!');
      } catch (e) {
        sessionStorage.removeItem('prefillGoal');
      }
    }
  }, [searchParams, loadGoals]);

  const openCreateDialog = () => {
    setEditingGoal(null);
    setGoalData({ title: '', description: '', target_date: '', steps: [], notes: '' });
    setNewStep('');
    setDialogOpen(true);
  };

  const openEditDialog = (goal) => {
    setEditingGoal(goal);
    setGoalData({
      title: goal.title,
      description: goal.description,
      target_date: goal.target_date || '',
      steps: goal.steps?.map(s => ({ id: s.id, text: s.text, completed: s.completed })) || [],
      notes: goal.notes || ''
    });
    setNewStep('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingGoal) {
      // Update existing goal
      try {
        const updateData = {
          title: goalData.title,
          description: goalData.description,
          target_date: goalData.target_date || null,
          steps: goalData.steps.map(s => ({
            id: s.id || undefined,
            text: s.text,
            completed: s.completed || false
          }))
        };
        await goalsAPI.update(editingGoal.id, updateData);
        toast.success('Goal updated!');
        setDialogOpen(false);
        setEditingGoal(null);
        loadGoals();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update goal');
      }
    } else {
      // Create new goal
      try {
        await goalsAPI.create({
          ...goalData,
          steps: goalData.steps.map(s => ({ text: s.text || s, completed: false }))
        });
        toast.success('Goal created!');
        setDialogOpen(false);
        loadGoals();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create goal');
      }
    }
    
    setGoalData({ title: '', description: '', target_date: '', steps: [], notes: '' });
    setNewStep('');
  };

  const handleAddStep = () => {
    if (newStep.trim()) {
      const newStepObj = editingGoal 
        ? { text: newStep.trim(), completed: false }
        : newStep.trim();
      setGoalData({ ...goalData, steps: [...goalData.steps, newStepObj] });
      setNewStep('');
    }
  };

  const handleRemoveStep = (index) => {
    setGoalData({
      ...goalData,
      steps: goalData.steps.filter((_, i) => i !== index)
    });
  };

  const handleStepTextChange = (index, newText) => {
    const updatedSteps = [...goalData.steps];
    if (typeof updatedSteps[index] === 'object') {
      updatedSteps[index] = { ...updatedSteps[index], text: newText };
    } else {
      updatedSteps[index] = newText;
    }
    setGoalData({ ...goalData, steps: updatedSteps });
  };

  const handleMoveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= goalData.steps.length) return;
    
    const updatedSteps = [...goalData.steps];
    const [movedStep] = updatedSteps.splice(index, 1);
    updatedSteps.splice(newIndex, 0, movedStep);
    setGoalData({ ...goalData, steps: updatedSteps });
  };

  const handleReorderStepInGoal = async (goalId, index, direction) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= goal.steps.length) return;
    
    const updatedSteps = [...goal.steps];
    const [movedStep] = updatedSteps.splice(index, 1);
    updatedSteps.splice(newIndex, 0, movedStep);
    
    try {
      await goalsAPI.update(goalId, { steps: updatedSteps });
      setGoals(goals.map(g => 
        g.id === goalId ? { ...g, steps: updatedSteps } : g
      ));
    } catch (error) {
      toast.error('Failed to reorder steps');
    }
  };

  const handleToggleStep = async (goalId, stepIndex) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;

    const updatedSteps = goal.steps.map((step, i) => 
      i === stepIndex ? { ...step, completed: !step.completed } : step
    );
    
    // Auto-calculate progress based on completed steps
    const completedCount = updatedSteps.filter(s => s.completed).length;
    const totalCount = updatedSteps.length;
    const newProgress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    try {
      await goalsAPI.update(goalId, { steps: updatedSteps, progress: newProgress });
      setGoals(goals.map(g => 
        g.id === goalId ? { ...g, steps: updatedSteps, progress: newProgress } : g
      ));
    } catch (error) {
      toast.error('Failed to update step');
    }
  };

  const handleCompleteGoal = async () => {
    const goalId = completeConfirm.goalId;
    try {
      await goalsAPI.update(goalId, { completed: true, progress: 100 });
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      toast.success('Goal completed! Amazing work!');
      setCompleteConfirm({ open: false, goalId: null });
      loadGoals();
    } catch (error) {
      toast.error('Failed to complete goal');
    }
  };

  const handleReopenGoal = async (goalId) => {
    try {
      const goal = goals.find(g => g.id === goalId);
      await goalsAPI.update(goalId, { completed: false, progress: goal?.progress || 0 });
      toast.success('Goal reopened!');
      loadGoals();
    } catch (error) {
      toast.error('Failed to reopen goal');
    }
  };

  const handleDeleteGoal = async () => {
    const goalId = deleteConfirm.goalId;
    try {
      await goalsAPI.delete(goalId);
      toast.success('Goal deleted');
      setDeleteConfirm({ open: false, goalId: null });
      loadGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const toggleExpand = (goalId) => {
    setExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  const getStepText = (step) => typeof step === 'object' ? step.text : step;

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="goals-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif mb-2">Goals & Action Plans</h1>
            <p className="text-muted-foreground">Set intentions and break them into actionable steps</p>
          </div>
          <Button onClick={openCreateDialog} className="rounded-full" data-testid="create-goal-btn">
            <Plus size={16} className="mr-2" />
            New Goal
          </Button>
        </div>

        {/* Goal Dialog (Create/Edit) */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingGoal ? 'Edit Goal' : 'Create New Goal'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="goal-form">
              <div>
                <Label htmlFor="goal-title">What do you want to achieve?</Label>
                <Input
                  id="goal-title"
                  placeholder="e.g., Establish a morning routine"
                  data-testid="goal-title-input"
                  value={goalData.title}
                  onChange={(e) => setGoalData({ ...goalData, title: e.target.value })}
                  required
                  className="mt-2 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="goal-description">Why does this matter to you?</Label>
                <Textarea
                  id="goal-description"
                  placeholder="What will achieving this goal mean for you?"
                  data-testid="goal-description-input"
                  value={goalData.description}
                  onChange={(e) => setGoalData({ ...goalData, description: e.target.value })}
                  required
                  className="mt-2 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="goal-target-date">Target Date (Optional)</Label>
                <Input
                  id="goal-target-date"
                  type="date"
                  data-testid="goal-date-input"
                  value={goalData.target_date}
                  onChange={(e) => setGoalData({ ...goalData, target_date: e.target.value })}
                  className="mt-2 rounded-xl"
                />
              </div>
              
              {/* Action Steps */}
              <div>
                <Label>Action Steps</Label>
                <p className="text-xs text-muted-foreground mb-2">Break your goal into smaller, actionable steps. Drag to reorder priority.</p>
                
                {goalData.steps.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {goalData.steps.map((step, index) => (
                      <div key={`step-${step.slice(0,20)}-${index}`} className="flex items-center gap-1 bg-muted/50 p-2 rounded-lg group">
                        <div className="flex flex-col">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-50 hover:opacity-100"
                            onClick={() => handleMoveStep(index, -1)}
                            disabled={index === 0}
                          >
                            <ArrowUp size={12} />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-50 hover:opacity-100"
                            onClick={() => handleMoveStep(index, 1)}
                            disabled={index === goalData.steps.length - 1}
                          >
                            <ArrowDown size={12} />
                          </Button>
                        </div>
                        <span className="text-xs text-muted-foreground w-5 text-center">{index + 1}</span>
                        <Input
                          value={getStepText(step)}
                          onChange={(e) => handleStepTextChange(index, e.target.value)}
                          className="flex-1 h-8 text-sm rounded-lg border-0 bg-transparent focus-visible:ring-1"
                          data-testid={`step-input-${index}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 flex-shrink-0 opacity-50 hover:opacity-100"
                          onClick={() => handleRemoveStep(index)}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a step..."
                    value={newStep}
                    onChange={(e) => setNewStep(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddStep())}
                    className="rounded-xl"
                  />
                  <Button type="button" variant="outline" onClick={handleAddStep} className="rounded-full">
                    Add
                  </Button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="goal-notes">
                  <span className="flex items-center gap-2">
                    <Lightbulb size={14} className="text-primary" />
                    Notes from <ClairName /> (Optional)
                  </span>
                </Label>
                <p className="text-xs text-muted-foreground mb-2">Tips, plans, or advice to help you achieve this goal</p>
                <Textarea
                  id="goal-notes"
                  placeholder="Add any notes, plans, or advice you want to remember..."
                  data-testid="goal-notes-input"
                  value={goalData.notes}
                  onChange={(e) => setGoalData({ ...goalData, notes: e.target.value })}
                  className="mt-2 rounded-xl"
                  rows={3}
                />
              </div>

              <Button type="submit" className="w-full rounded-full" data-testid="goal-submit-btn">
                {editingGoal ? 'Save Changes' : 'Create Goal'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading goals...</div>
        ) : goals.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl">
            <Target className="mx-auto mb-4 text-muted-foreground opacity-50" size={48} />
            <h3 className="text-xl font-serif mb-2">No goals yet</h3>
            <p className="text-muted-foreground mb-6">Create your first goal to start tracking your progress</p>
            <Button onClick={openCreateDialog} className="rounded-full">
              <Plus size={16} className="mr-2" />
              Create Your First Goal
            </Button>
          </Card>
        ) : (
          <>
            {/* Active Goals */}
            {activeGoals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-serif">Active Goals</h2>
                {activeGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    expanded={expandedGoals[goal.id]}
                    onToggleExpand={() => toggleExpand(goal.id)}
                    onToggleStep={handleToggleStep}
                    onReorderStep={handleReorderStepInGoal}
                    onEdit={() => openEditDialog(goal)}
                    onComplete={() => setCompleteConfirm({ open: true, goalId: goal.id })}
                    onDelete={() => setDeleteConfirm({ open: true, goalId: goal.id })}
                  />
                ))}
              </div>
            )}

            {/* Completed Goals */}
            {completedGoals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-serif text-muted-foreground">Completed</h2>
                {completedGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    expanded={expandedGoals[goal.id]}
                    onToggleExpand={() => toggleExpand(goal.id)}
                    onToggleStep={handleToggleStep}
                    onReorderStep={handleReorderStepInGoal}
                    onEdit={() => openEditDialog(goal)}
                    onReopen={() => handleReopenGoal(goal.id)}
                    onDelete={() => setDeleteConfirm({ open: true, goalId: goal.id })}
                    completed
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Complete Confirmation */}
      <AlertDialog open={completeConfirm.open} onOpenChange={(open) => setCompleteConfirm({ open, goalId: completeConfirm.goalId })}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Complete this goal?</AlertDialogTitle>
            <AlertDialogDescription>
              This is a moment worth celebrating. Ready to mark this goal as complete?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={handleCompleteGoal} className="rounded-full bg-accent hover:bg-accent/90">
              Yes, I did it!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ open, goalId: deleteConfirm.goalId })}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete this goal?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this goal and all its action steps.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGoal} className="rounded-full bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function GoalCard({ goal, expanded, onToggleExpand, onToggleStep, onReorderStep, onEdit, onComplete, onReopen, onDelete, completed }) {
  const completedSteps = goal.steps?.filter(s => s.completed).length || 0;
  const totalSteps = goal.steps?.length || 0;
  // Progress is calculated from steps completion
  const calculatedProgress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : (goal.progress || 0);

  return (
    <Card className={`rounded-2xl border-border shadow-md overflow-hidden ${completed ? 'opacity-70' : ''}`} data-testid={`goal-${goal.id}`}>
      {/* Goal Header */}
      <div 
        className="p-6 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {completed ? (
                <CheckCircle2 className="text-accent flex-shrink-0" size={24} />
              ) : (
                <Target className="text-primary flex-shrink-0" size={24} />
              )}
              <h3 className="text-lg font-serif">{goal.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground ml-9">{goal.description}</p>
            
            {/* Meta info */}
            <div className="flex items-center gap-4 mt-3 ml-9 text-xs text-muted-foreground">
              {goal.target_date && (
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {new Date(goal.target_date).toLocaleDateString()}
                </span>
              )}
              {totalSteps > 0 && (
                <span>{completedSteps}/{totalSteps} steps completed</span>
              )}
              <span className="font-medium">{calculatedProgress}% progress</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              data-testid={`edit-goal-${goal.id}`}
            >
              <Pencil size={16} />
            </Button>
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 ml-9">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${calculatedProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-border p-6 bg-muted/20">
          {/* Action Steps */}
          {goal.steps && goal.steps.length > 0 ? (
            <div className="mb-6">
              <Label className="text-sm mb-3 block">
                Action Steps — {completedSteps} of {totalSteps} complete
              </Label>
              <div className="space-y-2">
                {goal.steps.map((step, index) => (
                  <div 
                    key={step.id || index}
                    className={`flex items-center gap-2 p-3 rounded-xl transition-colors ${
                      step.completed ? 'bg-accent/10' : 'bg-background hover:bg-muted/50'
                    }`}
                  >
                    {/* Reorder buttons */}
                    {!completed && totalSteps > 1 && (
                      <div className="flex flex-col -my-1">
                        <button
                          type="button"
                          className="p-0.5 opacity-40 hover:opacity-100 disabled:opacity-20 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); onReorderStep(goal.id, index, -1); }}
                          disabled={index === 0}
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          type="button"
                          className="p-0.5 opacity-40 hover:opacity-100 disabled:opacity-20 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); onReorderStep(goal.id, index, 1); }}
                          disabled={index === totalSteps - 1}
                        >
                          <ArrowDown size={12} />
                        </button>
                      </div>
                    )}
                    
                    {/* Checkbox area */}
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => !completed && onToggleStep(goal.id, index)}
                    >
                      {step.completed ? (
                        <CheckCircle2 className="text-accent flex-shrink-0" size={18} />
                      ) : (
                        <Circle className="text-muted-foreground flex-shrink-0" size={18} />
                      )}
                      <span className={`text-sm ${step.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {step.text}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-6 text-sm text-muted-foreground">
              <p>No action steps yet. Click &ldquo;Edit Goal&rdquo; to add steps and track your progress.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button 
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="rounded-full"
            >
              <Pencil size={16} className="mr-2" />
              Edit Goal
            </Button>
            {!completed && onComplete && (
              <Button 
                onClick={(e) => { e.stopPropagation(); onComplete(); }}
                className="rounded-full bg-accent hover:bg-accent/90"
              >
                <CheckCircle2 size={16} className="mr-2" />
                Mark Complete
              </Button>
            )}
            {completed && onReopen && (
              <Button 
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onReopen(); }}
                className="rounded-full"
              >
                Reopen Goal
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="rounded-full text-destructive hover:text-destructive"
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </Button>
          </div>

          {/* Notes from CLaiR */}
          {goal.notes && (
            <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/10">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb size={16} className="text-primary" />
                <span className="text-sm font-medium">Notes from <ClairName /></span>
              </div>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{goal.notes}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
